import React, {PureComponent} from 'react';
import styled from 'styled-components';

import * as api from '../lib/api';
import {withUser} from '../lib/user';
import Loading from './Loading';
import Error from './Error';
import FullScreenMessage from './FullScreenMessage';
import ActivityCard from './ActivityCard';
import Card from './Card';
import Nl2br from './Nl2br';

//an optimistic entry is "confirmed" once the real feed contains a log for the same action, object and new value.
//until then we keep showing the optimistic entry so a "just saved" change never briefly vanishes while the backend's
//async audit write is still going on, once confirmed we drop it so it isn't shown twice
export function optimisticLogConfirmed(optimistic, logs) {
  return logs.some(log =>
    log.action === optimistic.action
    && log.objectKey === optimistic.objectKey
    && (log.next || [])[0] === (optimistic.next || [])[0]
  );
}

class AuditLog extends PureComponent {
  state = {
    loading: true,
    failed: false,
    logs: []
  };

  async componentDidMount() {
    const {tags, credentials, onLogsLoaded} = this.props;

    try {
      const logs = await api.getAuditLogs(tags, credentials);
      this.setState({logs});

      //hand the real logs to the parent so it can drop only the optimistic entries the feed has caught up to
      //(clearing unconditionally would make a comment vanish if its async audit write hasn't persisted yet)
      if (onLogsLoaded) {
        onLogsLoaded(logs);
      }
    }
    catch (e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  render() {
    const {children, formatValue = val => val, optimisticLogs = []} = this.props;
    const {loading, failed, logs} = this.state;

    if (loading) {
      return <Loading message="Loading history..." />;
    }

    if (failed) {
      return <Error message="Failed to load history" />;
    }

    //optimistic entries are the most recent, and the feed is newest-first, so they go on top. Drop any that the
    //real feed already contains so a just-saved change shows immediately but never renders twice.
    const displayLogs = [
      ...optimisticLogs.filter(optimistic => !optimisticLogConfirmed(optimistic, logs)),
      ...logs
    ];

    if (displayLogs.length < 1) {
      return <FullScreenMessage message="No changes have been made" />;
    }

    return (
      <Container>
        {displayLogs.map(log => {
          const nextValues = log.next.map(next => formatValue(log, next)).filter(Boolean);
          const prevValues = log.previous.map(prev => formatValue(log, prev)).filter(Boolean);

          return (
            <div key={log.id}>
              <ActivityCard user={log.user} activity={children(log)} date={new Date(log.timestamp)}>
                {(log.comment || nextValues.length > 0 || prevValues.length > 0) && (
                  <Card.Content>
                    <DiffContainer>
                      {log.comment && (
                        <Comment>
                          <h4>Reason for Change</h4>
                          <Nl2br>{log.comment}</Nl2br>
                        </Comment>
                      )}
                      {nextValues.map((value, i) => (
                        <NextValue key={i}>{value}</NextValue>
                      ))}
                      {prevValues.map((value, i) => (
                        <PrevValue key={i}>{value}</PrevValue>
                      ))}
                    </DiffContainer>
                  </Card.Content>
                )}
              </ActivityCard>
            </div>
          );
        })}
      </Container>
    );
  }
}

const Container = styled.div`
  > div {
    margin-bottom: 1.5rem;

    :last-child {
      margin-bottom: 0;
    }
  }
`;

const DiffContainer = styled.ul`
  margin: 0;
  padding: 0;
`;

const NextValue = styled.li`
  padding: 0.5rem 1rem;
  display: block;
  background: #d4edda;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
`;

const PrevValue = styled.li`
  padding: 0.5rem 1rem;
  display: block;
  background: #f8d7da;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;

  :last-child {
    margin-bottom: 0;
  }
`;

const Comment = styled.div`
  background: #f5f5f8;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 0.5rem;

  h4 {
    margin: 0 0 0.25rem 0;
  }
`;

export default withUser(AuditLog);